import { Button, Tooltip } from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { useEffect } from "react"
import { useHistory } from "react-router-dom"
import { create } from "zustand"
import { EstabelecimentoAberto } from "../../services/clientefiel"
import useAppStore from "../../stores/AppStore"
import useStatusEstabelecimentoStore from "../../stores/StatusEstabelecimentoStore"

/* Situação da loja (Aberto/Fechado/Pausado).

   Fica num store próprio porque dois pontos do cabeçalho mostram o mesmo dado — o botão,
   junto ao seletor de Operação, e o pontinho colorido da BarraPerfil no mobile. Com o estado
   dentro de um componente, mover o botão de lugar duplicaria o polling de 2 min. */

const useStatusLojaStore = create(set => ({
    pausado: false,
    online: null,
    definir: dados => set(dados),
}))

function calcularStatus(online, pausado) {
    if (online == null) return null
    if (pausado) return { label: "Pausado", cor: "#f5a623" }
    if (!online) return { label: "Fechado", cor: "#e53935" }
    return { label: "Aberto", cor: "#2e9e5b" }
}

async function carregar() {
    const estabelecimentoId =
        useAppStore.getState().configuracoes?.user?.parametros?.estabelecimento?.id
    if (!estabelecimentoId) return
    try {
        const aberto = await EstabelecimentoAberto(estabelecimentoId)
        if (aberto && !aberto.retornoErro && typeof aberto.online === "boolean") {
            useStatusLojaStore.getState().definir({
                online: aberto.online,
                pausado: typeof aberto.pausado === "boolean" ? aberto.pausado : false,
            })
        }
    } catch {}
}

// Só o primeiro consumidor montado liga o intervalo; os demais reaproveitam o mesmo dado.
let consumidores = 0
let intervalo = null

/** Situação atual da loja, ou null enquanto ainda não se sabe. */
export function useStatusLoja() {
    const pausado = useStatusLojaStore(s => s.pausado)
    const online = useStatusLojaStore(s => s.online)
    const estabelecimento = useAppStore(s => s.configuracoes?.user?.parametros?.estabelecimento)
    const estabelecimentoId = estabelecimento?.id
    const versao = useStatusEstabelecimentoStore(s => s.versao)

    // Semente vinda das configurações: evita o botão piscar vazio até a primeira resposta.
    useEffect(() => {
        if (!estabelecimento) return
        if (useStatusLojaStore.getState().online == null) {
            useStatusLojaStore.getState().definir({
                online: estabelecimento.online ?? null,
                pausado: estabelecimento.pausado ?? false,
            })
        }
    }, [estabelecimento])

    useEffect(() => {
        if (!estabelecimentoId) return undefined
        carregar()
        consumidores += 1
        if (consumidores === 1) intervalo = setInterval(carregar, 120000)
        return () => {
            consumidores -= 1
            if (consumidores === 0) {
                clearInterval(intervalo)
                intervalo = null
            }
        }
    }, [estabelecimentoId, versao])

    return { status: calcularStatus(online, pausado) }
}

const useStyles = makeStyles({
    botao: {
        height: 34,
        minWidth: 108,
        borderRadius: 8,
        color: "#fff",
        fontSize: 12,
        fontWeight: 700,
        textTransform: "none",
        paddingInline: "1rem",
        boxShadow: "none",
        "&:hover": { boxShadow: "none", filter: "brightness(0.95)" },
    },
})

/** Botão de situação da loja. Fica ao lado do seletor de Operação, que é o contexto dele. */
export default function BotaoStatusLoja() {
    const classes = useStyles()
    const history = useHistory()
    const { status } = useStatusLoja()
    const solicitarAtualizacao = useStatusEstabelecimentoStore(s => s.solicitarAtualizacao)

    if (!status) return null

    return (
        <Tooltip arrow title="Situação do estabelecimento. Clique para ajustar os horários do delivery.">
            <Button
                variant="contained"
                onClick={() => {
                    solicitarAtualizacao()
                    history.push("/servicos/modulo/horarios-delivery")
                }}
                className={classes.botao}
                style={{ backgroundColor: status.cor }}
            >
                {status.label}
            </Button>
        </Tooltip>
    )
}
